import { useState, useEffect, useMemo, useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Button } from 'reactstrap'

import { showSuccess } from 'slices/notificationSlice'
import { ENABLE_DIALOG_ERROR_STATUS_CODES } from 'slices/utils'
import { csvImportTargets, selectWorkspacesStatus } from 'slices/workspacesSlice'

import { CustomModal, FileInput } from 'components/common'
import type { CharacterCodeType } from 'components/common/CharacterCodeSelect/CharacterCodeSelect'
import { CHARACTER_CODE_TYPES, CharacterCodeSelect } from 'components/common/CharacterCodeSelect/CharacterCodeSelect'

import { useTargetsCsv, FORMAT_FILE_NAME } from 'hooks/useTargetsCsv'

type Props = {
  workDate?: string
  isOpen: boolean
  onSuccess: () => void
  onCancel: () => void
}

const ImportTargetsDialog = ({ isOpen, onSuccess, onCancel, workDate }: Props) => {
  const { workspaceId } = useParams<'workspaceId'>()
  const [submitted, setSubmitted] = useState(false)
  const [modalErrorMessage, setModalErrorMessage] = useState<string>()
  const [file, setFile] = useState<File | null>(null)
  const [characterCode, setCharacterCode] = useState<CharacterCodeType>(CHARACTER_CODE_TYPES.SHIFT_JIS)

  const dispatch = useDispatch()

  const { isRequesting, errorMessage } = useSelector(selectWorkspacesStatus, shallowEqual)

  const { handleDataFormat, formatDate } = useTargetsCsv()

  const disabled = useMemo(() => !(file && /\.csv$/i.test(file.name)), [file])

  const handleApprove = useCallback(() => {
    if (!file || !workspaceId) {
      setModalErrorMessage('ファイルを選択してください')
      return
    }

    const formData = new FormData()
    formData.append('fileData', file)
    formData.append('encoding', characterCode)
    workDate && formData.append('workDate', workDate)
    dispatch(csvImportTargets(workspaceId, formData))
    setSubmitted(true)
  }, [characterCode, dispatch, file, workDate, workspaceId])

  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      setModalErrorMessage(undefined)
      onSuccess()
    } else if (!ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
      setModalErrorMessage('作業目標のインポートに失敗しました')
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, onSuccess, dispatch])

  const handleCancelClick = useCallback(() => {
    setModalErrorMessage(undefined)
    setSubmitted(false)
    onCancel()
  }, [setModalErrorMessage, setSubmitted, onCancel])

  const handleCharacterCodeChange = (value: CharacterCodeType) => {
    setCharacterCode(value)
  }

  return (
    <CustomModal
      isOpen={isOpen}
      title={`作業目標インポート ${workDate ? `: ${formatDate(workDate)}` : ''}`}
      approveDisabled={disabled}
      errorMessage={modalErrorMessage}
      onCancel={handleCancelClick}
      onApprove={handleApprove}
      onHideNotification={() => setModalErrorMessage(undefined)}
      submitName="targets-import-submit"
    >
      <div>
        <div className="mb-3">
          {`下記${FORMAT_FILE_NAME}をダウンロードして、同じフォーマットでCSVアップロードをしてください。`}
        </div>
        <div className="w-60 mb-3">
          <CharacterCodeSelect characterCode={characterCode} onChange={handleCharacterCodeChange} />
        </div>
        <ul className="mb-3">
          <li>
            目標はワークスペース毎に一括入力が可能です。ヘッダーの作業名が登録されている作業名と一致している必要があります。
          </li>
          <li>空白の場合は操作しません。作業目標を削除したい場合は0を入力してアップロードしてください。</li>
          {workDate && <li>該当日の目標のみ取り込みます｡</li>}
        </ul>
        <Button
          color="link"
          className="mb-3 ps-0 shadow-none text-decoration-none"
          onClick={() => {
            handleDataFormat()
            onCancel()
            dispatch(showSuccess())
          }}
        >
          {`${FORMAT_FILE_NAME}をダウンロード`}
        </Button>
        <FileInput id="memberFile" accept=".csv" onChange={f => setFile(f)} />
      </div>
    </CustomModal>
  )
}

export default ImportTargetsDialog
